import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { getCompanyID, getCompanyVerificationStatus, reviewCompanyVerification } from '../../api/web';
import { documentTypeLabel } from '../../utils/companyDocuments';

const toTitle = (s) => documentTypeLabel(s);

const formatDocumentExpiry = (doc) => {
    if (!doc?.expires_at) return '—';
    const formatted = moment(doc.expires_at);
    return formatted.isValid() ? formatted.format('Do MMM, YYYY') : '—';
};

const CompanyVerification = (props) => {
    const pacraId = props?.match?.params?.id;

    const [initialLoading, setInitialLoading] = useState(true);
    const [company, setCompany] = useState(null);
    const [companyMeta, setCompanyMeta] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [missingDocuments, setMissingDocuments] = useState([]);
    const [commentByDocType, setCommentByDocType] = useState({});
    const [companyRejectComment, setCompanyRejectComment] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    /**
     * @param {{ fullPage?: boolean }} opts - fullPage: true only on first load / route change.
     *  After approve/reject, use background refresh so the page is not replaced by a blocking spinner.
     */
    const refresh = (opts = {}) => {
        const fullPage = Boolean(opts.fullPage);
        if (fullPage) {
            setInitialLoading(true);
        } else {
            setRefreshing(true);
        }
        Promise.all([
            getCompanyVerificationStatus(pacraId),
            getCompanyID(pacraId),
        ])
            .then(([verificationRes, companyRes]) => {
                if (verificationRes.error) {
                    toast.error(verificationRes.payload, { position: 'top-right', autoClose: 5000 });
                    setCompany(null);
                    setDocuments([]);
                    setMissingDocuments([]);
                } else {
                    const c = verificationRes?.payload?.company;
                    setCompany(c ?? null);
                    setDocuments(Array.isArray(c?.documents) ? c.documents : []);
                    setMissingDocuments(Array.isArray(c?.missing_documents) ? c.missing_documents : []);
                }

                setCompanyMeta(companyRes?.error ? null : companyRes?.payload ?? null);
            })
            .catch((err) => {
                console.error('CompanyVerification refresh failed', err);
                toast.error('Failed to refresh verification data. Try again.', {
                    position: 'top-right',
                    autoClose: 5000,
                });
            })
            .finally(() => {
                setInitialLoading(false);
                setRefreshing(false);
            });
    };

    useEffect(() => {
        refresh({ fullPage: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pacraId]);

    useEffect(() => {
        if (!companyRejectComment && company?.verification_comment) {
            setCompanyRejectComment(company.verification_comment);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company]);

    const targetPacraId = company?.pacra_id ?? pacraId;

    const verificationStatusBadge = useMemo(() => {
        const status = (company?.verification_status ?? '').toLowerCase();
        const map = {
            pending_documents: { cls: 'bg-info text-white', label: 'Pending Documents' },
            under_review: { cls: 'bg-warning text-white', label: 'Under Review' },
            verified: { cls: 'bg-success text-white', label: 'Verified' },
            rejected: { cls: 'bg-danger text-white', label: 'Rejected' },
            suspended: { cls: 'bg-secondary text-white', label: 'Suspended' },
        };
        return map[status] ?? (status ? { cls: 'bg-secondary text-white', label: status } : null);
    }, [company]);

    const docStatusBadge = (status) => {
        const s = (status ?? '').toLowerCase();
        const map = {
            approved: 'bg-success text-white',
            rejected: 'bg-danger text-white',
            pending: 'bg-info text-white',
        };
        const cls = map[s] ?? 'bg-secondary text-white';
        return <span className={`badge ${cls}`}>{toTitle(s)}</span>;
    };

    const onApproveDoc = (docType) => {
        if (actionLoading || !targetPacraId) return;
        setActionLoading(true);
        reviewCompanyVerification('patch', {
            company_pacra_id: targetPacraId,
            action: 'approve',
            document_type: docType,
        })
            .then((res) => {
                if (res.error) return toast.error(res.payload, { position: 'top-right', autoClose: 5000 });
                toast.success('Document approved.', { position: 'top-right', autoClose: 3000 });
                refresh();
            })
            .finally(() => setActionLoading(false));
    };

    const onRejectDoc = (docType) => {
        const comment = (commentByDocType[docType] ?? '').trim();
        if (!comment) return toast.error('Comment is required when rejecting.', { position: 'top-right' });
        if (actionLoading || !targetPacraId) return;
        setActionLoading(true);
        reviewCompanyVerification('patch', {
            company_pacra_id: targetPacraId,
            action: 'reject',
            comment,
            document_type: docType,
        })
            .then((res) => {
                if (res.error) return toast.error(res.payload, { position: 'top-right', autoClose: 5000 });
                toast.success('Document rejected.', { position: 'top-right', autoClose: 3000 });
                refresh();
            })
            .finally(() => setActionLoading(false));
    };

    const onApproveAllDocs = () => {
        if (actionLoading || !targetPacraId) return;
        setActionLoading(true);
        reviewCompanyVerification('patch', {
            company_pacra_id: targetPacraId,
            action: 'approve',
        })
            .then((res) => {
                if (res.error) return toast.error(res.payload, { position: 'top-right', autoClose: 5000 });
                toast.success('Company documents approved.', { position: 'top-right', autoClose: 3000 });
                refresh();
            })
            .finally(() => setActionLoading(false));
    };

    const onRejectAllDocs = () => {
        const comment = (companyRejectComment ?? '').trim();
        if (!comment) return toast.error('Comment is required when rejecting.', { position: 'top-right' });
        if (actionLoading || !targetPacraId) return;
        setActionLoading(true);
        reviewCompanyVerification('patch', {
            company_pacra_id: targetPacraId,
            action: 'reject',
            comment,
        })
            .then((res) => {
                if (res.error) return toast.error(res.payload, { position: 'top-right', autoClose: 5000 });
                toast.success('Company documents rejected.', { position: 'top-right', autoClose: 3000 });
                refresh();
            })
            .finally(() => setActionLoading(false));
    };

    const timelineEvents = useMemo(() => {
        const events = [];
        const companyLevelComment = company?.verification_comment?.toString().trim();

        if (companyMeta?.date_added) {
            events.push({
                id: 'company-created',
                at: companyMeta.date_added,
                title: 'Company account created',
                message: companyMeta.name ? `Company: ${companyMeta.name}` : 'Company account created',
            });
        }

        if (Array.isArray(documents)) {
            documents.forEach((doc) => {
                const docType = doc?.document_type;
                if (doc?.uploaded_at) {
                    events.push({
                        id: `doc-upload-${doc.id ?? docType}`,
                        at: doc.uploaded_at,
                        title: 'Document uploaded',
                        message: `Document: ${toTitle(docType)}`,
                    });
                }

                const status = (doc?.status ?? '').toLowerCase();
                const eventAt = doc?.reviewed_at ?? doc?.uploaded_at ?? null;
                if (status === 'approved' && eventAt) {
                    events.push({
                        id: `doc-approved-${doc.id ?? docType}`,
                        at: eventAt,
                        title: 'Document approved',
                        message: `Document: ${toTitle(docType)}`,
                    });
                }

                if (status === 'rejected' && eventAt) {
                    const docLevelComment = doc?.rejection_reason?.toString().trim();
                    const reason = docLevelComment ? docLevelComment : (companyLevelComment ? companyLevelComment : null);
                    events.push({
                        id: `doc-rejected-${doc.id ?? docType}`,
                        at: eventAt,
                        title: 'Document rejected',
                        message: reason
                            ? `Document: ${toTitle(docType)}. Reason: ${reason}`
                            : `Document: ${toTitle(docType)}`,
                        comment: reason,
                    });
                }
            });
        }

        if (companyMeta?.verified_at) {
            events.push({
                id: 'company-verified',
                at: companyMeta.verified_at,
                title: 'Company verified',
                message: companyMeta.name ? `Company: ${companyMeta.name}` : 'Company verified',
            });
        }

        return events.filter((e) => e.at).sort((a, b) => new Date(a.at) - new Date(b.at));
    }, [companyMeta, company, documents]);

    if (initialLoading) {
        return (
            <div className="content-body">
                <div className="container d-flex justify-content-center ht-100p">
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div>
                        <p className="mg-t-10">Loading company verification</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content-body">
            <div className="container pd-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/company">Company</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Verification</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-0 tx-spacing--1">Company Verification</h4>
                    </div>
                    <div className="d-none d-md-block">
                        <Link to={`/company/${pacraId}`} className="btn btn-sm pd-x-15 btn-secondary btn-uppercase">
                            <i className="wd-10 mg-r-5 fa fa-arrow-left"></i> Back to company details
                        </Link>
                    </div>
                </div>

                {!company ? (
                    <div className="card"><div className="card-body"><p className="tx-danger mg-b-0">Unable to load company verification documents.</p></div></div>
                ) : (
                    <>
                        {refreshing ? (
                            <div className="alert alert-light border mg-b-15 d-flex align-items-center" role="status">
                                <div className="spinner-border spinner-border-sm mg-r-10" aria-hidden="true" />
                                <span className="tx-13">Updating verification data…</span>
                            </div>
                        ) : null}
                        <div className="card mg-b-20">
                            <div className="card-body">
                                <div className="row row-sm">
                                    <div className="col-md-6">
                                        <p className="mg-b-2"><strong>Company:</strong> {company?.name ?? '—'}</p>
                                        <p className="mg-b-2"><strong>Pacra ID:</strong> {company?.pacra_id ?? pacraId ?? '—'}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="mg-b-2"><strong>Status:</strong> {verificationStatusBadge ? <span className={`badge ${verificationStatusBadge.cls}`}>{verificationStatusBadge.label}</span> : '—'}</p>
                                        <p className="mg-b-0"><strong>Missing Documents:</strong> {missingDocuments.length ? missingDocuments.map(toTitle).join(', ') : 'None'}</p>
                                    </div>
                                </div>
                                <div className="row row-sm mg-t-10">
                                    <div className="col-md-6">
                                        <label className="mg-b-5">Reject Comment (for Reject All)</label>
                                        <textarea className="form-control" rows={4} style={{ width: '100%', minHeight: 120, resize: 'vertical' }} value={companyRejectComment} onChange={(e) => setCompanyRejectComment(e.target.value)} />
                                    </div>
                                    <div className="col-md-6 d-flex align-items-end">
                                        <div className="d-flex" style={{ flexWrap: 'wrap', gap: 8 }}>
                                            <button className="btn btn-sm btn-success" disabled={actionLoading} onClick={onApproveAllDocs}>Approve All</button>
                                            <button className="btn btn-sm btn-danger" disabled={actionLoading} onClick={onRejectAllDocs}>Reject All</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-header d-flex align-items-center justify-content-between">
                                <h6 className="mg-b-0">Uploaded Documents</h6>
                                <div className="d-flex tx-18">
                                    <a
                                        href="javascript:void(0)"
                                        className="link-03 lh-0"
                                        onClick={() => {
                                            if (!refreshing) refresh();
                                        }}
                                        title="Refresh"
                                    >
                                        <i className={`icon ion-md-refresh${refreshing ? ' tx-color-03' : ''}`} />
                                    </a>
                                </div>
                            </div>
                            <div className="card-body">
                                {documents.length === 0 ? (
                                    <p className="tx-color-03 mg-b-0">No documents found for this company.</p>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-sm" style={{ width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th className="wd-20p">Document Type</th>
                                                    <th className="wd-15p">Status</th>
                                                    <th className="wd-15p">Expires</th>
                                                    <th className="wd-20p">Uploaded</th>
                                                    <th className="wd-20p">Reviewed</th>
                                                    <th className="wd-20p">File</th>
                                                    <th className="wd-30p" style={{ minWidth: 320 }}>Comment</th>
                                                    <th className="wd-25p">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {documents.map((doc) => {
                                                    const docType = doc.document_type;
                                                    const existingRejection = doc.rejection_reason;
                                                    const textareaVal = commentByDocType[docType] ?? (existingRejection ?? '');
                                                    return (
                                                        <tr key={doc.id ?? docType}>
                                                            <td>{toTitle(docType)}</td>
                                                            <td>{docStatusBadge(doc.status)}</td>
                                                            <td>{formatDocumentExpiry(doc)}</td>
                                                            <td>{doc.uploaded_at ? moment(doc.uploaded_at).format('Do MMM, YYYY HH:mm') : '—'}</td>
                                                            <td>{doc.reviewed_at ? moment(doc.reviewed_at).format('Do MMM, YYYY HH:mm') : '—'}</td>
                                                            <td>{doc.file_url ? <a href={doc.file_url} target="_blank" rel="noreferrer">View</a> : '—'}</td>
                                                            <td style={{ minWidth: 320 }}>
                                                                <textarea
                                                                    className="form-control"
                                                                    rows={2}
                                                                    style={{ width: '100%', minHeight: 70, resize: 'vertical' }}
                                                                    value={textareaVal}
                                                                    onChange={(e) => setCommentByDocType((prev) => ({ ...prev, [docType]: e.target.value }))}
                                                                    disabled={actionLoading}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2" style={{ flexWrap: 'wrap' }}>
                                                                    <button className="btn btn-sm btn-success" disabled={actionLoading || !docType} onClick={() => onApproveDoc(docType)}>Approve</button>
                                                                    <button className="btn btn-sm btn-danger" disabled={actionLoading || !docType} onClick={() => onRejectDoc(docType)}>Reject</button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="card mg-b-20">
                            <div className="card-header"><h6 className="mg-b-0">Verification Timeline</h6></div>
                            <div className="card-body">
                                {timelineEvents.length === 0 ? (
                                    <p className="tx-color-03 mg-b-0">No verification history available.</p>
                                ) : (
                                    <ul className="activity tx-13">
                                        {timelineEvents.map((evt) => (
                                            <li key={evt.id} className="activity-item">
                                                <div className="activity-body">
                                                    <p className="mg-b-2">
                                                        <span className="tx-semibold">{evt.title}</span>
                                                        {evt.at && <span className="tx-color-03 tx-12 mg-l-10">{moment(evt.at).format("Do MMM, YYYY HH:mm")}</span>}
                                                    </p>
                                                    {evt.message && <p className="mg-b-0 tx-color-03">{evt.message}</p>}
                                                    {evt.comment && <p className="mg-b-0 tx-color-03"><strong>Comment:</strong> {evt.comment}</p>}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CompanyVerification;

